import { useMemo, useState, type RefObject } from 'react';
import { UMR, UMToolbarIconButton, UMToolbarInput, type UMMapHandle } from '../../urbanmap/_components';
import { boundsOf } from './domain';
import type { Commune, LngLat } from './types';

interface Props {
  mapRef: RefObject<UMMapHandle>;
  communes: Commune[];
  communeOutlines: Record<string, LngLat[]>;
  onCommuneSelect?: (code: string) => void;
  /** Emprise de recentrage du bouton « Me localiser » (périmètre du projet). */
  home: LngLat[];
}

const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** Barre de recherche UrbanMap (Rechercher + Me localiser), en haut à gauche des vues carto. */
export function MapSearchBar({ mapRef, communes, communeOutlines, onCommuneSelect, home }: Props) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const hits = useMemo(() => {
    const q = norm(search.trim());
    if (!q) return [];
    return communes.filter((c) => norm(`${c.name} ${c.postalCode ?? ''} ${c.insee}`).includes(q)).slice(0, 6);
  }, [search, communes]);
  const goTo = (c: Commune) => {
    const shape = communeOutlines[c.insee];
    if (shape?.length) mapRef.current?.fitBounds(boundsOf(shape), 40);
    onCommuneSelect?.(c.insee);
    setSearch(c.name);
    setOpen(false);
  };

  return (
    <div
      className="pnv2-map-search"
      style={{
        position: 'absolute', top: 0, left: 0, zIndex: 20, display: 'flex', alignItems: 'center',
        gap: UMR.size.toolbarPadding, padding: UMR.size.toolbarPadding, background: UMR.white,
        borderBottom: `1px solid ${UMR.border}`, borderRight: `1px solid ${UMR.border}`, fontFamily: UMR.font,
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target instanceof HTMLInputElement && hits[0]) {
          e.preventDefault();
          goTo(hits[0]);
        }
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      <style>{'.pnv2-map-search .um-toolbar-input::placeholder{color:var(--nj-core-color-grey-600)}.pnv2-map-search .um-toolbar-input:focus{border:2px solid var(--nj-core-color-blue-600)!important}'}</style>
      <div style={{ position: 'relative' }}>
        <UMToolbarInput ariaLabel="Rechercher" placeholder="Rechercher" value={search} trailingIcon="search"
          onChange={(v) => { setSearch(v); setOpen(true); }} />
        {open && search.trim() && (
          <ul aria-label="Résultats de recherche" style={{
            position: 'absolute', top: UMR.size.inputHeight, left: 0, width: UMR.size.inputWidth, margin: 0, padding: 0,
            listStyle: 'none', background: UMR.white, border: `1px solid ${UMR.border}`, zIndex: 40, fontSize: 14,
          }}>
            {hits.length === 0 && <li style={{ padding: '8px 16px', color: UMR.textMuted }}>Aucun résultat</li>}
            {hits.map((c) => (
              <li key={c.insee}>
                <button type="button" onClick={() => goTo(c)}
                  style={{ padding: '8px 16px', cursor: 'pointer', width: '100%', textAlign: 'left', background: UMR.white, border: 0, font: 'inherit', color: UMR.black }}>
                {c.name} <span style={{ color: UMR.textMuted }}>({c.postalCode ?? c.insee})</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <UMToolbarIconButton icon="my_location" label="Me localiser"
        onClick={() => { if (home.length) mapRef.current?.fitBounds(boundsOf(home), 40); }} />
    </div>
  );
}
